import React, { useState } from "react";
import EyeIcon from "../../icons/eye.icon";
import EyeClosedIcon from "../../icons/eyeClosed.icon";
import { useInterpret } from "@xstate/react";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import Cookies from "js-cookie";
import Image from "next/image";
import { EmailRegex, PwdRegex, mobileRegex } from "../../core/login.core";
import { getDomain } from "../../core/cookie.core";
import registerCore from "../../core/register.core";
import FormAlert from "../../components/forms/alert.comp";
import { RegisterFormType, RegisterErrorMessage } from "../../types/register";
import { registerMachine } from "../../states/register.state";
import MainLayout from "../layout/MainLayout";
import PhoneInput from "../phoneInput.comp";
import { useTranslation } from "../../core/translation.core";

// We need this for UI purposes only and will transform it for the API
interface RegisterFormUI {
  firstName: string;
  lastName: string;
  email: string;
  countryCode: string;
  mobileNumber: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
}

export default function RegisterPage() {
  const { t, msg } = useTranslation();
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const { query, pathname, push } = useRouter();
  const registerStateService = useInterpret(registerMachine);

  // Password visibility toggle states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Get user's language preference for country names
  const isChineseLanguage = t(msg.login.register).match(/[\u3400-\u9FBF]/) !== null;

  // Form handling with react-hook-form
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormUI>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      countryCode: "852",
      mobileNumber: "",
      password: "",
      confirmPassword: "",
      agreeToTerms: false,
    },
  });

  const handleClick = () => {
    push({
      pathname: pathname,
      query: { ...query, page: "login" },
    });
  };

  const handleAlertReset = () => {
    setErrorMessage("");
    setSuccessMessage("");
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleCountryCodeChange = (e) => {
    // Remove the + sign for the internal form value
    setValue("countryCode", e.target.value.replace("+", ""));
  };

  const onSubmit = async (formData: RegisterFormUI) => {
    try {
      // Verify terms agreement
      if (!formData.agreeToTerms) {
        setErrorMessage(t(msg.general.errSubmit2));
        return;
      }

      // Mobile number is required for registration (shop regType is emailAndTel)
      const mobileDigits = formData.mobileNumber.replace(/\D/g, "");
      if (!mobileDigits) {
        setErrorMessage(t(msg.login.mobileNumberR));
        return;
      }

      // Prepare data for API - transform form data to match RegisterFormType
      const apiData: RegisterFormType = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        countryCode: parseInt(formData.countryCode, 10),
        mobileNumber: parseInt(mobileDigits, 10),
        password: formData.password,
        confirmPassword: formData.confirmPassword,
      };

      // Call register API
      const res = await registerCore.register(apiData);

      // Success handling and redirection
      if (res && "token" in res) {
        Cookies.set("temp-token", "local:" + res.token, {
          domain: getDomain(),
          expires: 1,
          sameSite: "strict",
        });
        setSuccessMessage(t(msg.login.registerSuccess));
        registerStateService.send("SUBMIT");
        // SMS OTP was sent at registration; the account stays unverified until it is entered
        setTimeout(() => {
          push({
            pathname: "/auth/verifyMobileOtp",
            query: {
              userId: res.userId,
              mobileLast4: mobileDigits.slice(-4),
              mode: "verifyOtp",
            },
          });
        }, 2000);
      } else {
        setErrorMessage(t(msg.general.errSubmit1));
      }
    } catch (e: any) {
      // Handle specific error cases from API
      const errorMessage = e.message as RegisterErrorMessage;
      switch (errorMessage) {
        case "userAlreadyExists":
          setErrorMessage(t(msg.login.userAlreadyExists));
          break;
        case "mobileAlreadyExists":
          setErrorMessage(t(msg.login.mobileAlreadExists));
          break;
        case "emailAlreadyExists":
          setErrorMessage(t(msg.login.emailAlreadyExists));
          break;
        default:
          setErrorMessage(t(msg.general.errSubmit1));
      }
    }
  };

  return (
    <MainLayout title={t(msg.login.register)} url="/auth/register" description="Register for a new Guru Guru HK account">
      <section className="w-full min-h-screen bg-guru-floral-white ">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col lg:flex-row w-full gap-6 mx-auto">
            {/* Left side - Image and benefits */}
            <div className="w-full lg:w-1/2 bg-white p-5 md:p-7 flex items-center justify-center rounded-xl shadow-md">
              <div className="flex flex-col items-center">
                <h1 className="text-2xl md:text-3xl font-bold text-center mb-6">歡迎加入我們 ：）</h1>

                <div className="flex justify-center mb-8 relative w-full h-56 md:h-64">
                  <Image src="/images/logo-square.webp" alt="reg-guru" layout="fill" objectFit="contain" priority />
                </div>

                <div className="mb-6 text-base">
                  <div className="flex items-center mb-3">
                    <span className="text-yellow-400 text-lg mr-2">✓</span>
                    <span>購買店內所有貨品</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-yellow-400 text-lg mr-2">✓</span>
                    <span>消費升級以獲得更多折扣</span>
                  </div>
                </div>
                <p className="grow-0 block py-2 font-serif text-sm md:text-base text-center pt-20">
                  <span>{t(msg.login.actionLogin)}</span>
                  <button id="login-link" onClick={handleClick} className="ml-2 text-primary underline cursor-pointer">
                    {t(msg.login.login)}
                  </button>
                </p>
              </div>
            </div>

            {/* Right side - Form */}
            <div className="w-full lg:w-1/2 bg-white p-5 md:p-7 flex flex-col justify-center rounded-xl shadow-md mt-4 lg:mt-0">
              <div className="w-full max-w-md mx-auto">
                <FormAlert successMessage={successMessage} errorMessage={errorMessage} handleReset={handleAlertReset} />

                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-4">
                      <div className="flex-1">
                        <label htmlFor="firstName" className="block text-sm md:text-base font-medium mb-1">
                          {t(msg.login.firstName)}
                        </label>
                        <input
                          id="firstName"
                          type="text"
                          className={`w-full p-2.5 text-sm md:text-base border rounded focus:outline-none focus:ring-2 ${
                            errors.firstName ? "border-red-500 focus:ring-red-200" : "focus:ring-gray-200"
                          }`}
                          {...register("firstName", { required: t(msg.login.firstNameR) })}
                        />
                        {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>}
                      </div>

                      <div className="flex-1">
                        <label htmlFor="lastName" className="block text-sm md:text-base font-medium mb-1">
                          {t(msg.login.lastName)}
                        </label>
                        <input
                          id="lastName"
                          type="text"
                          className={`w-full p-2.5 text-sm md:text-base border rounded focus:outline-none focus:ring-2 ${
                            errors.lastName ? "border-red-500 focus:ring-red-200" : "focus:ring-gray-200"
                          }`}
                          {...register("lastName", { required: t(msg.login.lastNameR) })}
                        />
                        {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName.message}</p>}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm md:text-base font-medium mb-1">
                        {t(msg.login.email)}
                      </label>
                      <input
                        id="email"
                        type="email"
                        className={`w-full p-2.5 text-sm md:text-base border rounded focus:outline-none focus:ring-2 ${
                          errors.email ? "border-red-500 focus:ring-red-200" : "focus:ring-gray-200"
                        }`}
                        {...register("email", {
                          required: t(msg.login.emailR),
                          pattern: {
                            value: EmailRegex,
                            message: t(msg.login.emailInvalid),
                          },
                        })}
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                      <p className="text-xs opacity-60 mt-1">{t(msg.login.paragraph1)}</p>
                    </div>

                    {/* Fixed Phone Input Section */}
                    <div>
                      <PhoneInput
                        countryCodeValue={`+${watch("countryCode")}`}
                        onCountryCodeChange={handleCountryCodeChange}
                        phoneValue={watch("mobileNumber")}
                        onPhoneChange={(e) => setValue("mobileNumber", e.target.value)}
                        phoneLabel={t(msg.login.mobileNumber)}
                        isChineseLanguage={isChineseLanguage}
                        countryClassName="w-full p-2.5 text-sm md:text-base border rounded focus:outline-none focus:ring-2 focus:ring-gray-200"
                        phoneClassName={`w-full p-2.5 text-sm md:text-base border rounded focus:outline-none focus:ring-2 ${
                          errors.mobileNumber ? "border-red-500 focus:ring-red-200" : "focus:ring-gray-200"
                        }`}
                        errorMessage={errors.mobileNumber?.message}
                        required={true}
                      />
                    </div>

                    <div>
                      <label htmlFor="password" className="block text-sm md:text-base font-medium mb-1">
                        {t(msg.login.password)}
                      </label>
                      <div className="relative">
                        <input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          className={`w-full p-2.5 text-sm md:text-base border rounded focus:outline-none focus:ring-2 ${
                            errors.password ? "border-red-500 focus:ring-red-200" : "focus:ring-gray-200"
                          }`}
                          {...register("password", {
                            required: t(msg.login.passwordR),
                            pattern: {
                              value: PwdRegex,
                              message: t(msg.login.pwdStrengthNotice),
                            },
                          })}
                        />
                        <button
                          type="button"
                          onClick={togglePasswordVisibility}
                          className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400"
                        >
                          {showPassword ? <EyeClosedIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                        </button>
                      </div>
                      {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
                      <p className="text-xs opacity-60 mt-1">{t(msg.login.paragraph2)}</p>
                    </div>

                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm md:text-base font-medium mb-1">
                        {t(msg.login.confirmPassword)}
                      </label>
                      <div className="relative">
                        <input
                          id="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          className={`w-full p-2.5 text-sm md:text-base border rounded focus:outline-none focus:ring-2 ${
                            errors.confirmPassword ? "border-red-500 focus:ring-red-200" : "focus:ring-gray-200"
                          }`}
                          {...register("confirmPassword", {
                            validate: (val: string) => {
                              if (watch("password") !== val) {
                                return t(msg.login.confirmPasswordWrong);
                              }
                              return true;
                            },
                          })}
                        />
                        <button
                          type="button"
                          onClick={toggleConfirmPasswordVisibility}
                          className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400"
                        >
                          {showConfirmPassword ? <EyeClosedIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                        </button>
                      </div>
                      {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>}
                    </div>

                    <div className="flex items-start">
                      <div className="flex h-5 items-center">
                        <input
                          id="agreeToTerms"
                          type="checkbox"
                          className={`h-4 w-4 rounded border-gray-300 ${
                            errors.agreeToTerms ? "border-red-500" : ""
                          } text-gray-600 focus:ring-gray-500`}
                          {...register("agreeToTerms", {
                            required: t(msg.general.errSubmit2),
                          })}
                        />
                      </div>
                      <div className="ml-2">
                        <label htmlFor="agreeToTerms" className="block text-sm md:text-base text-gray-700">
                          {t(msg.login.paragraph1)} <span className="text-gray-900 font-medium">{t(msg.menu.tnc)}</span>
                        </label>
                        {errors.agreeToTerms && <p className="text-red-500 text-xs mt-1">{errors.agreeToTerms.message}</p>}
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-gray-200 text-gray-700 p-3 rounded mt-6 text-base font-medium hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
                    >
                      {t(msg.login.register)}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </MainLayout>
  );
}
